const chapter = {
  id: 'general-organic-chemistry',
  slug: 'general-organic-chemistry',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE / NEET',
  title: {
    english: 'General Organic Chemistry (GOC)',
        hindi: 'सामान्य कार्बनिक रसायन (GOC)',
  },
  estReadMinutes: 65,
  difficulty: {
    english: 'Hard',
        hindi: 'कठिन',
  },
  weightage: {
    english: 'Foundation for all organic chapters; 3-4 questions',
        hindi: 'सभी जैविक अध्यायों के लिए फाउंडेशन; 3-4 प्रश्न',
  },
  syllabusTags: ['Organic Chemistry', 'Class-11', 'JEE Main', 'NEET'],
  sections: [
    {
      id: 'iupac-classification',
      title: {
        english: 'Classification & IUPAC Nomenclature',
        hindi: 'वर्गीकरण और IUPAC नामकरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Organic compounds classified into **acyclic (open-chain)** and **cyclic** (further: alicyclic, aromatic, heterocyclic).',
        hindi: 'कार्बनिक यौगिकों को **एसाइक्लिक (ओपन-चेन)** और **साइक्लिक** (आगे: एलिसाइक्लिक, एरोमैटिक, हेट्रोसाइक्लिक) में वर्गीकृत किया गया है।',
            },
            {
              english: '**IUPAC name structure**: Substituent + Locant + Parent (longest chain) + Suffix (functional group).',
        hindi: '**IUPAC नाम संरचना**: सब्स्टीट्यूएंट + लोकेंट + पैरेंट (सबसे लंबी श्रृंखला) + प्रत्यय (कार्यात्मक समूह)।',
            },
            {
              english: 'Priority of functional groups (highest → lowest): **-COOH > -COOR > -CONH₂ > -CN > -CHO > >C=O > -OH > -NH₂ > -C=C- > -C≡C- > halogen / nitro / alkoxy (prefix only)**.',
        hindi: 'कार्यात्मक समूहों की प्राथमिकता (उच्चतम → निम्नतम): **-COOH > -COOR > -CONH₂ > -CN > -CHO > >C=O > -OH > -NH₂ > -C=C- > -C≡C- > हैलोजन / नाइट्रो / एल्कोक्सी (केवल उपसर्ग)**।',
            },
            {
              english: 'When multiple groups → **principal functional group** is suffix; others as prefixes.',
        hindi: 'जब एकाधिक समूह → **प्रमुख कार्यात्मक समूह** प्रत्यय होता है; अन्य उपसर्गों के रूप में।',
            },
            {
              english: 'Numbering: lowest locant to **principal group → multiple bonds → substituents (alphabetical) → first point of difference**.',
        hindi: 'क्रमांकन: **मुख्य समूह के लिए निम्नतम स्थान → एकाधिक बांड → प्रतिस्थापन (वर्णमाला) → अंतर का पहला बिंदु**।',
            },
          ],
        },
        {
          type: 'example',
          problem: {
            english: 'IUPAC name of $CH_3-CH(OH)-CH_2-COOH$.',
        hindi: '$CH_3-CH(OH)-CH_2-COOH$ का IUPAC नाम।',
          },
          solution: {
            english: '4 carbons + COOH (suffix -oic acid) + OH (prefix hydroxy) → **3-hydroxybutanoic acid**.',
        hindi: '4 कार्बन + COOH (प्रत्यय -ओइक एसिड) + OH (उपसर्ग हाइड्रॉक्सी) → **3-हाइड्रॉक्सीबुटानोइक एसिड**।',
          },
        },
      ],
    },
    {
      id: 'hybridisation-bonding',
      title: {
        english: 'Hybridisation & Bond Properties',
        hindi: 'संकरण और बन्ध गुणधर्म',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**sp³** — 4 σ bonds, tetrahedral, 109.5°, alkanes (C-H of CH₄).',
        hindi: '**sp³** - 4 σ बांड, टेट्राहेड्रल, 109.5°, अल्केन्स (CH₄ का C-H)।',
            },
            {
              english: '**sp²** — 3 σ + 1 π, trigonal planar, 120°, alkenes (C=C), carbonyls (C=O), benzene.',
        hindi: '**sp²** - 3 σ + 1 π, ट्राइगोनल प्लेनर, 120°, एल्केन्स (C=C), कार्बोनिल्स (C=O), बेंजीन।',
            },
            {
              english: '**sp** — 2 σ + 2 π, linear, 180°, alkynes (C≡C), nitriles (C≡N), allenes (terminal C of $CH_2=C=CH_2$).',
        hindi: '**sp** - 2 σ + 2 π, रैखिक, 180°, एल्काइन्स (C≡C), नाइट्राइल्स (C≡N), एलीन्स ($CH_2=C=CH_2$ का टर्मिनल C)।',
            },
            {
              english: 'Bond properties: **% s-character** ↑ → bond length ↓, bond strength ↑, electronegativity of carbon ↑, acidity of attached H ↑.',
        hindi: 'बॉन्ड गुण: **% s-वर्ण** ↑ → बॉन्ड लंबाई ↓, बॉन्ड ताकत ↑, कार्बन की इलेक्ट्रोनगेटिविटी ↑, संलग्न H की अम्लता ↑।',
            },
            {
              english: 'Order of acidity of terminal H: **HC≡CH (sp, 50% s) > CH₂=CH₂ (sp², 33%) > CH₄ (sp³, 25%)**.',
        hindi: 'टर्मिनल एच की अम्लता का क्रम: **HC≡CH (sp³, 50% s) > CH₂=CH₂ (sp², 33%) > CH₄ (sp³, 25%)**।',
            },
          ],
        },
      ],
    },
    {
      id: 'electronic-effects',
      title: {
        english: 'Electronic Effects',
        hindi: 'इलेक्ट्रॉनिक प्रभाव',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Inductive effect (I)** — permanent polarisation of σ bond; transmitted along C-chain; **diminishes with distance** (~1/3 per bond).',
        hindi: '**प्रेरक प्रभाव (I)** - σ बांड का स्थायी ध्रुवीकरण; सी-श्रृंखला के साथ प्रेषित; **दूरी के साथ कम हो जाती है** (~1/3 प्रति बांड)।',
            },
            {
              english: '• **-I (electron-withdrawing)**: $-NO_2 > -CN > -F > -Cl > -Br > -I > -OH > -OR > -COOH > -COOR > -C_6H_5 > -H$.',
        hindi: '• **-I (इलेक्ट्रॉन-निकासी)**: $-NO_2 > -CN > -F > -Cl > -Br > -I > -OH > -OR > -COOH > -COOR > -C_6H_5 > -H$।',
            },
            {
              english: '• **+I (electron-donating)**: $-O^- > -COO^- > -C(CH_3)_3 > -CH(CH_3)_2 > -C_2H_5 > -CH_3 > -H$.',
        hindi: '• **+मैं (इलेक्ट्रॉन-दान)**: $-O^- > -COO^- > -C(CH_3)_3 > -CH(CH_3)_2 > -C_2H_5 > -CH_3 > -H$।',
            },
            {
              english: '**Mesomeric / Resonance effect (M / R)** — electron delocalisation through π / lone pair; **acts through conjugated system**; does NOT diminish.',
        hindi: '**मेसोमेरिक / रेजोनेंस प्रभाव (एम / आर)** - π / लोन जोड़ी के माध्यम से इलेक्ट्रॉन डेलोकलाइजेशन; **संयुग्मित प्रणाली के माध्यम से कार्य करता है**; कम नहीं होता.',
            },
            {
              english: '• **+M (donating)**: $-NH_2 > -OH > -OR > -NHCOR > -OCOR > -X$.',
        hindi: '• **+एम (दान)**: $-NH_2 > -OH > -OR > -NHCOR > -OCOR > -X$।',
            },
            {
              english: '• **-M (withdrawing)**: $-NO_2 > -CN > -CHO > -COR > -COOH > -COOR > -CONH_2$.',
        hindi: '• **-एम (वापस लेना)**: $-NO_2 > -CN > -CHO > -COR > -COOH > -COOR > -CONH_2$।',
            },
            {
              english: '**Hyperconjugation (no-bond resonance / σ-π conjugation)** — interaction of α C-H σ bond with adjacent empty/π orbital. **More α-H → more hyperconjugation → more stable**. Stabilises carbocations, free radicals & alkenes.',
        hindi: '**हाइपरकंजुगेशन (नो-बॉन्ड अनुनाद / σ-π संयुग्मन)** - आसन्न खाली/π ऑर्बिटल के साथ α C-H σ बॉन्ड की इंटरेक्शन। **अधिक α-H → अधिक हाइपरसंयुग्मन → अधिक स्थिर**। कार्बोकेशन, फ्री रेडिकल्स और एल्केन्स को स्थिर करता है।',
            },
            {
              english: '**Electromeric effect (E)** — temporary shift of π electrons in presence of an attacking reagent (+E or -E). Reversible; vanishes when reagent removed.',
        hindi: '**इलेक्ट्रोमेरिक प्रभाव (ई)** - एक हमलावर अभिकर्मक (+ई या -ई) की उपस्थिति में π इलेक्ट्रॉनों का अस्थायी बदलाव। प्रतिवर्ती; अभिकर्मक हटा दिए जाने पर गायब हो जाता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'reaction-intermediates',
      title: {
        english: 'Reaction Intermediates',
        hindi: 'अभिक्रिया मध्यवर्ती',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Carbocation (C⁺)** — sp², 6 e⁻, planar, **electrophile**.',
        hindi: '**कार्बोकेशन (C⁺)** - sp², 6 e⁻, तलीय, **इलेक्ट्रोफाइल**।',
            },
            {
              english: '• Stability: **3° > 2° > 1° > methyl** (hyperconjugation + +I of alkyl).',
        hindi: '• स्थिरता: **3° > 2° > 1° > मिथाइल** (हाइपरकन्जुगेशन + +एल्काइल का I)।',
            },
            {
              english: '• Allyl & benzyl cations highly stabilised by resonance.',
        hindi: '• एलिल और बेंजाइल धनायन अनुनाद द्वारा अत्यधिक स्थिर होते हैं।',
            },
            {
              english: '• Tropylium ($C_7H_7^+$) — aromatic, 6 π e⁻, very stable.',
        hindi: '• ट्रॉपिलियम ($C_7H_7^+$) - सुगंधित, 6 π e⁻, बहुत स्थिर।',
            },
            {
              english: '**Carbanion (C⁻)** — sp³, 8 e⁻, pyramidal, **nucleophile**.',
        hindi: '**कार्बनियन (C⁻)** - sp³, 8 e⁻, पिरामिडनुमा, **न्यूक्लियोफाइल**।',
            },
            {
              english: '• Stability **opposite** to carbocations: **methyl > 1° > 2° > 3°** (alkyl groups destabilise -ve charge by +I).',
        hindi: '• स्थिरता **कार्बोकेशन के विपरीत**: **मिथाइल > 1° > 2° > 3°** (एल्काइल समूह +I द्वारा -ve चार्ज को अस्थिर करते हैं)।',
            },
            {
              english: '• Stabilised by EWGs (-NO₂, -CN, -COR) via -M.',
        hindi: '• -M के माध्यम से EWGs (-NO₂, -CN, -COR) द्वारा स्थिर।',
            },
            {
              english: '**Free radical** — sp² (or pyramidal-flickering), 7 e⁻, neutral.',
        hindi: '**मुक्त मूलक** - sp² (या पिरामिड-झिलमिलाहट), 7 e⁻, तटस्थ।',
            },
            {
              english: '• Stability parallels carbocations: **3° > 2° > 1° > methyl**; benzyl & allyl very stable.',
        hindi: '• स्थिरता समानताएं कार्बोकेशन: **3° > 2° > 1° > मिथाइल**; बेंज़िल और एलिल बहुत स्थिर।',
            },
            {
              english: '**Carbene (R₂C:)** — neutral, 6 e⁻, divalent C.',
        hindi: '**कार्बेन (R₂C:)** - तटस्थ, 6 e⁻, द्विसंयोजक C.',
            },
            {
              english: '• **Singlet** (sp², paired e⁻, ~ 102°) → stereospecific addition.',
        hindi: '• **सिंगलेट** (sp², युग्मित e⁻, ~ 102°) → त्रिविम विशिष्ट जोड़।',
            },
            {
              english: '• **Triplet** (sp, unpaired e⁻, ~ 136°) → biradical, non-stereospecific.',
        hindi: '• **ट्रिप्लेट** (एसपी, अयुग्मित ई⁻, ~ 136°) → बिराडिकल, गैर-स्टीरियोस्पेसिफिक।',
            },
            {
              english: '**Nitrene (R-N:)** — N-analog of carbene; 6 e⁻ on N.',
        hindi: '**नाइट्रीन (आर-एन:)** - कार्बाइन का एन-एनालॉग; एन पर 6 ई⁻',
            },
          ],
        },
      ],
    },
    {
      id: 'electrophiles-nucleophiles',
      title: {
        english: 'Electrophiles, Nucleophiles & Reaction Types',
        hindi: 'इलेक्ट्रोफाइल, न्यूक्लियोफाइल और अभिक्रिया प्रकार',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Electrophile** — electron-deficient; accepts e⁻ pair (Lewis acid). Examples: $H^+, NO_2^+, SO_3, BF_3, AlCl_3, R^+, CH_3CO^+, Cl^+$.',
        hindi: '**इलेक्ट्रोफाइल** - इलेक्ट्रॉन की कमी; ई⁻ जोड़ी (लुईस एसिड) स्वीकार करता है। उदाहरण: $H^+, NO_2^+, SO_3, BF_3, AlCl_3, R^+, CH_3CO^+, Cl^+$.',
            },
            {
              english: '**Nucleophile** — electron-rich; donates e⁻ pair (Lewis base). Examples: $OH^-, CN^-, NH_3, H_2O, ROR, X^-, RMgX$.',
        hindi: '**न्यूक्लियोफाइल** - इलेक्ट्रॉन-समृद्ध; ई⁻ जोड़ी (लुईस बेस) दान करता है। उदाहरण: $OH^-, CN^-, NH_3, H_2O, ROR, X^-, RMgX$.',
            },
            {
              english: 'Nucleophilicity vs basicity — basicity = thermodynamic (binds H⁺); nucleophilicity = kinetic (attacks C). In **same period**, parallel; **down a group**, nucleophilicity ↑ (polarisability) but basicity ↓.',
        hindi: 'न्यूक्लियोफिलिसिटी बनाम बेसिकिटी - basicity = thermodynamic (binds H⁺); nucleophilicity = kinetic (attacks C). **समान अवधि** में, समानांतर; **एक समूह के नीचे**, न्यूक्लियोफिलिसिटी ↑ (ध्रुवीकरण) लेकिन मूलभूतता ↓।',
            },
            {
              english: 'Reaction types:',
        hindi: 'प्रतिक्रिया प्रकार:',
            },
            {
              english: '• **Substitution** — atom/group replaced ($SN_1, SN_2, SE, SR$).',
        hindi: '• **प्रतिस्थापन** — परमाणु/समूह प्रतिस्थापित ($SN_1, SN_2, SE, SR$)।',
            },
            {
              english: '• **Addition** — to multiple bonds ($AE, AN, AR$).',
        hindi: '• **अतिरिक्त** — एकाधिक बांडों के लिए ($AE, AN, AR$)।',
            },
            {
              english: '• **Elimination** — formation of multiple bonds ($E1, E2, E1cb$).',
        hindi: '• **उन्मूलन** — एकाधिक बांडों का निर्माण ($E1, E2, E1cb$)।',
            },
            {
              english: '• **Rearrangement** — atoms re-arrange (carbocation 1,2-H or 1,2-CH₃ shift).',
        hindi: '• **पुनर्व्यवस्था** - परमाणु पुनः व्यवस्थित होते हैं (कार्बोकेशन 1,2-एच या 1,2-सीएच₃ शिफ्ट)।',
            },
          ],
        },
      ],
    },
    {
      id: 'isomerism',
      title: {
        english: 'Isomerism',
        hindi: 'समावयवता',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Isomers — same molecular formula, different structures/properties.',
        hindi: 'आइसोमर्स - समान आणविक सूत्र, भिन्न structures/properties। **संरचनात्मक (संवैधानिक) आइसोमर्स**:',
            },
            {
              english: '**Structural (constitutional) isomers**:',
        hindi: '**संरचनात्मक (संवैधानिक) आइसोमर्स**:',
            },
            {
              english: '• **Chain** — different skeleton (n-butane vs isobutane).',
        hindi: '• **श्रृंखला** — विभिन्न कंकाल (एन-ब्यूटेन बनाम आइसोब्यूटेन)।',
            },
            {
              english: '• **Position** — same skeleton, different position of group (1- vs 2-propanol).',
        hindi: '• **स्थिति** - एक ही कंकाल, समूह की अलग स्थिति (1- बनाम 2-प्रोपेनॉल)।',
            },
            {
              english: '• **Functional** — different functional groups (ethanol vs dimethyl ether).',
        hindi: '• **कार्यात्मक** - विभिन्न कार्यात्मक समूह (इथेनॉल बनाम डाइमिथाइल ईथर)।',
            },
            {
              english: '• **Metamerism** — different alkyl groups on either side of polyvalent group ($CH_3-O-C_3H_7$ vs $C_2H_5-O-C_2H_5$).',
        hindi: '• **मेटामेरिज्म** - पॉलीवलेंट समूह के दोनों ओर अलग-अलग एल्काइल समूह ($CH_3-O-C_3H_7$ बनाम $C_2H_5-O-C_2H_5$)।',
            },
            {
              english: '• **Tautomerism** — dynamic equilibrium (keto ⇌ enol). Acetone (99.99% keto); acetylacetone (~76% enol due to intramolecular H-bond).',
        hindi: '• **टॉटोमेरिज्म** - गतिशील संतुलन (कीटो ⇌ एनोल)। एसीटोन (99.99% कीटो); एसिटाइलएसीटोन (इंट्रामोलेक्यूलर एच-बॉन्ड के कारण ~76% एनोल)।',
            },
            {
              english: '• **Ring-chain** ($C_3H_6$ — propene vs cyclopropane).',
        hindi: '• **रिंग-चेन** ($C_3H_6$ - प्रोपेन बनाम साइक्लोप्रोपेन)। **स्टीरियोइसोमेरिज्म** - समान कनेक्टिविटी, भिन्न स्थानिक व्यवस्था:',
            },
            {
              english: '**Stereoisomerism** — same connectivity, different spatial arrangement:',
        hindi: '**स्टीरियोइसोमेरिज़्म** - समान कनेक्टिविटी, भिन्न स्थानिक व्यवस्था:',
            },
            {
              english: '• **Geometrical (cis-trans, E/Z)** — restricted rotation; need 2 different groups on each sp² C.',
        hindi: '• **ज्यामितीय (सीआईएस-ट्रांस, E/Z)** - प्रतिबंधित रोटेशन; प्रत्येक sp² C पर 2 अलग-अलग समूहों की आवश्यकता है।',
            },
            {
              english: '• **Optical (enantiomers, diastereomers)** — molecular chirality (asymmetric C).',
        hindi: '• **ऑप्टिकल (एनेंटिओमर्स, डायस्टेरोमर्स)** - आणविक चिरैलिटी (असममित सी)।',
            },
            {
              english: '  – Enantiomers — non-superimposable mirror images; rotate plane-polarised light equally & oppositely.',
        hindi: '- एनैन्टीओमर्स - गैर-सुपरइम्पोज़ेबल दर्पण छवियां; समतल-ध्रुवीकृत प्रकाश को समान और विपरीत दिशा में घुमाएँ।',
            },
            {
              english: '  – Diastereomers — stereoisomers that are NOT mirror images.',
        hindi: '- डायस्टेरियोमर्स - स्टीरियोइसोमर्स जो दर्पण छवि नहीं हैं।',
            },
            {
              english: '  – **Racemic mixture** — 50:50 enantiomers; optically inactive.',
        hindi: '- **रेसमिक मिश्रण** - 50:50 एनैन्टीओमर्स; वैकल्पिक रूप से निष्क्रिय.',
            },
            {
              english: '  – **Meso** — chiral centres but internal plane of symmetry → optically inactive.',
        hindi: '- **मेसो** - चिरल केंद्र लेकिन समरूपता का आंतरिक तल → प्रकाशिक रूप से निष्क्रिय।',
            },
            {
              english: '  – Total optical isomers for $n$ chiral centres = $2^n$ (less if meso).',
        hindi: '- $n$ चिरल centres = (less if meso) के लिए कुल ऑप्टिकल आइसोमर्स।',
            },
          ],
        },
      ],
    },
    {
      id: 'acidity-basicity',
      title: {
        english: 'Acidity & Basicity Trends',
        hindi: 'अम्लता और क्षारकता रुझान',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Acidity of carboxylic acids**: EWGs ↑ acidity (stabilise $-COO^-$); EDGs ↓.',
        hindi: '**कार्बोक्जिलिक एसिड की अम्लता**: ईडब्ल्यूजी ↑ अम्लता (स्थिर $-COO^-$); ईडीजी ↓.',
            },
            {
              english: '• Order: $HCOOH > CH_3COOH$; $CCl_3COOH > CHCl_2COOH > CH_2ClCOOH > CH_3COOH$.',
        hindi: '• आदेश: $HCOOH > CH_3COOH$; $CCl_3COOH > CHCl_2COOH > CH_2ClCOOH > CH_3COOH$.',
            },
            {
              english: '• Position effect in chloro-acids: **α-Cl ≫ β-Cl > γ-Cl** (inductive falls with distance).',
        hindi: '• क्लोरो-एसिड में स्थिति प्रभाव: **α-Cl ≫ β-Cl > γ-Cl** (दूरी के साथ आगमनात्मक गिरावट)।',
            },
            {
              english: '**Acidity of alcohols & water**: $H_2O > 1° > 2° > 3°$ alcohol (steric + hyperconjugative destabilisation of alkoxide).',
        hindi: '**अल्कोहल और पानी की अम्लता**: $H_2O > 1° > 2° > 3°$ अल्कोहल (स्टेरिक + एल्कोऑक्साइड का हाइपरकोन्जुगेटिव अस्थिरता)।',
            },
            {
              english: '**Acidity of phenols** > alcohols (resonance stabilisation of phenoxide). EWGs at o/p ↑ acidity (picric acid).',
        hindi: '**फिनोल्स की अम्लता** > अल्कोहल (फेनोक्साइड का अनुनाद स्थिरीकरण)। o/p पर EWGs ↑ अम्लता (पिक्रिक एसिड)।',
            },
            {
              english: '**Basicity of amines** (in aq.): **2° > 1° > 3° > NH₃** (compromise between +I, solvation & steric).',
        hindi: '**ऐमीनों की क्षारकता** (जल में): **2° > 1° > 3° > NH₃** (+I, सॉल्वेशन और स्टेरिक के बीच समझौता)।',
            },
            {
              english: '• In gas phase: $3° > 2° > 1° > NH_3$ (only +I, no solvation).',
        hindi: '• गैस चरण में: $3° > 2° > 1° > NH_3$ (केवल +I, कोई सॉल्वेशन नहीं)।',
            },
            {
              english: 'Aromatic amines weaker than aliphatic (lone pair delocalised into ring). EWGs ↓ basicity (m-nitroaniline < aniline).',
        hindi: 'ऐरोमैटिक ऐमीन एलिफैटिक की तुलना में कमजोर है (अकेला जोड़ा रिंग में स्थानीयकृत है)। ईडब्ल्यूजी ↓ मूलभूतता (एम-नाइट्रोएनिलिन <एनिलिन)।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Acidity of CH: sp (50\\%s)} > \\text{sp}^2 > \\text{sp}^3',
      desc: {
        english: '',
        hindi: 'CH की अम्लता: sp (50\\%s) > sp 2 > sp 3',
      },
    },
    {
      latex: '\\text{Carbocation stability: 3° > 2° > 1° > methyl}',
      desc: {
        english: '',
        hindi: 'कार्बोकेशन स्थिरता: 3° > 2° > 1° > मिथाइल',
      },
    },
    {
      latex: '\\text{Carbanion stability: methyl > 1° > 2° > 3° (opposite)}',
      desc: {
        english: '',
        hindi: 'कार्बोनियन स्थिरता: मिथाइल > 1° > 2° > 3° (विपरीत)',
      },
    },
    {
      latex: '\\text{+I order: -O}^- > -\\text{COO}^- > -\\text{C(CH}_3)_3 > -\\text{CH}_3',
      desc: {
        english: '',
        hindi: '+मैं आदेश देता हूं: -O - > -COO - > -C(CH 3) 3 > -CH 3',
      },
    },
    {
      latex: '\\text{-I order: -NO}_2 > -\\text{CN} > -\\text{F} > -\\text{Cl} > -\\text{COOH}',
      desc: {
        english: '',
        hindi: '-मैं आदेश देता हूं: -NO 2 > -CN > -F > -Cl > -COOH',
      },
    },
    {
      latex: '\\text{Optical isomers} = 2^n \\text{ (less if meso)}',
      desc: {
        english: '',
        hindi: 'ऑप्टिकल आइसोमर्स = 2 एन (मेसो से कम)',
      },
    },
    {
      latex: '\\text{Amine basicity (aq): 2° > 1° > 3° > NH}_3',
      desc: {
        english: '',
        hindi: 'अमीन क्षारकता (aq): 2° > 1° > 3° > NH 3',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Stability',
      problem: {
        english: 'Most stable carbocation among: methyl, ethyl, isopropyl, tert-butyl?',
        hindi: 'इनमें से सबसे स्थिर कार्बोकेशन: मिथाइल, एथिल, आइसोप्रोपिल, टर्ट-ब्यूटाइल?',
      },
      answer: {
        english: 'tert-butyl (3°)',
        hindi: 'टर्ट-ब्यूटाइल (3°)',
      },
      solution: {
        english: 'More α-H + more +I → more stable.',
        hindi: 'अधिक α-H + अधिक +I → अधिक स्थिर।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Acidity',
      problem: {
        english: 'Which is most acidic: HCOOH, CH₃COOH, CH₂ClCOOH, CCl₃COOH?',
        hindi: 'सबसे अधिक अम्लीय कौन सा है: HCOOH, CH₃COOH, CH₂ClCOOH, CCl₃COOH?',
      },
      answer: {
        english: 'CCl₃COOH',
        hindi: 'CCl₃COOH',
      },
      solution: {
        english: 'Three Cl groups exert strongest -I → stabilises carboxylate.',
        hindi: 'तीन सीएल समूह सबसे मजबूत -I लगाते हैं → कार्बोक्सिलेट को स्थिर करते हैं।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Hybridisation',
      problem: {
        english: 'Hybridisation of central C in $CH_2=C=CH_2$ (allene):',
        hindi: '$CH_2=C=CH_2$ (एलीन) में केंद्रीय C का संकरण:',
      },
      answer: {
        english: 'sp',
        hindi: 'एसपी',
      },
      solution: {
        english: 'Two cumulated double bonds; central C is linear.',
        hindi: 'दो संचयी दोहरे बांड; केंद्रीय C रैखिक है.',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Tautomerism',
      problem: {
        english: 'Which compound exhibits maximum enol content?',
        hindi: 'कौन सा यौगिक अधिकतम एनोल सामग्री प्रदर्शित करता है?',
      },
      answer: {
        english: 'Acetylacetone (1,3-diketones)',
        hindi: 'एसिटाइलएसीटोन (1,3-डाइकेटोन)',
      },
      solution: {
        english: 'Intramolecular H-bond stabilises enol form (~76%).',
        hindi: 'इंट्रामोल्युलर एच-बॉन्ड एनोल फॉर्म (~76%) को स्थिर करता है।',
      },
    },
    {
      year: 2019,
      exam: 'JEE Main',
      tag: 'Basicity',
      problem: {
        english: 'Order of basicity in aqueous medium of $CH_3NH_2,\\ (CH_3)_2NH,\\ (CH_3)_3N,\\ NH_3$.',
        hindi: '$CH_3NH_2,\\ (CH_3)_2NH,\\ (CH_3)_3N,\\ NH_3$ के जलीय माध्यम में बुनियादीता का क्रम।',
      },
      answer: {
        english: '$(CH_3)_2NH > CH_3NH_2 > (CH_3)_3N > NH_3$',
        hindi: '$(CH_3)_2NH > CH_3NH_2 > (CH_3)_3N > NH_3$',
      },
      solution: {
        english: 'Compromise: +I, H-bonding (solvation), steric.',
        hindi: 'समझौता: +I, H-बॉन्डिंग (सॉल्वेशन), स्टेरिक। C-X कार्बन के संकरण पर आधारित: **एल्काइल** (, R-X), **** ( adj से ), **एरिल** (X रिंग पर)।',
      },
    },
  ],
  next: {
    slug: 'hydrocarbons',
    title: 'Hydrocarbons',
  },
};

export default chapter;
